import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import HelpModal from '../../../components/HelpModal/HelpModal'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import changeRequestService from '../../../services/changeRequestService'
import projectService from '../../../services/projectService'
import employeeMasterService from '../../../services/employeeMasterService'
import { 
  Save, 
  X, 
  FileText,
  AlertCircle,
  RotateCcw,
  Clock,
  DollarSign,
  Users,
  Settings,
  CheckCircle2,
  Wrench,
  HelpCircle
} from 'lucide-react'

const ChangeRequestForm = ({ mode = 'create', changeRequestId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const actualChangeRequestId = changeRequestId || id
  const actualMode = mode || (actualChangeRequestId ? (window.location.pathname.includes('/edit/') ? 'edit' : 'view') : 'create')
  
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [changeRequestDataLoading, setChangeRequestDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  
  const isEditMode = actualMode === 'edit'
  const isViewMode = actualMode === 'view'
  const isReadOnly = isViewMode
  
  const [masterData, setMasterData] = useState({
    projects: [],
    employees: []
  })

  const [formData, setFormData] = useState({
    // Basic Information
    changeRequestNumber: '',
    project: '',
    projectNumber: '',
    projectName: '',
    title: '',
    description: '',
    changeType: 'Scope Change',
    priority: 'Medium',
    status: 'Draft',
    requestedBy: '',
    requestedByCode: '',
    requestedByName: '',
    requestDate: new Date().toISOString().split('T')[0],
    
    // Approval Information
    approver: '',
    approverName: '',
    approvalDate: '',
    approvalComments: '',
    
    // Impact Analysis
    impactAnalysis: {
      scope: '',
      timeline: '',
      budget: 0,
      resources: '',
      risks: ''
    },
    
    // Implementation Details
    implementationPlan: '',
    estimatedEffort: 0,
    estimatedEffortUnit: 'Days',
    estimatedCost: 0,
    currency: 'INR',
    requestedStartDate: '',
    requestedEndDate: '',
    actualStartDate: '',
    actualEndDate: '',
    assignedTo: '',
    assignedToCode: '',
    assignedToName: '',
    
    // Additional Information
    attachments: [],
    notes: '',
    remarks: ''
  })

  const [errors, setErrors] = useState({})

  // Load master data
  useEffect(() => {
    if (!masterDataLoaded) {
      loadMasterData()
    }
  }, [])

  // Load change request data for edit/view mode
  useEffect(() => {
    if ((isEditMode || isViewMode) && actualChangeRequestId && masterDataLoaded) {
      if (initialData) {
        populateFormData(initialData)
      } else {
        loadChangeRequestData()
      }
    }
  }, [actualChangeRequestId, isEditMode, isViewMode, masterDataLoaded])

  const loadMasterData = async () => {
    try {
      setMasterDataLoaded(false)
      const [
        projectsRes,
        employeesRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000 }),
        employeeMasterService.getAllEmployees({ limit: 1000 })
      ])

      const getProjectsData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.projects) {
            return res.projects
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      setMasterData(prev => ({
        ...prev,
        projects: getProjectsData(projectsRes),
        employees: getEmployeesData(employeesRes)
      }))
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      toast.error('Failed to load master data')
      setMasterDataLoaded(true)
    }
  }

  const loadChangeRequestData = async () => {
    try {
      setChangeRequestDataLoading(true)
      const response = await changeRequestService.getChangeRequestById(actualChangeRequestId)
      
      if (response.success && response.data) {
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load change request')
        navigate('/projects/change-requests')
      }
    } catch (error) {
      console.error('Error loading change request:', error)
      toast.error('Failed to load change request')
      navigate('/projects/change-requests')
    } finally {
      setChangeRequestDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    setFormData({
      changeRequestNumber: data.changeRequestNumber || '',
      project: data.project?._id || data.project || '',
      projectNumber: data.projectNumber || data.project?.projectNumber || '',
      projectName: data.projectName || data.project?.projectName || '',
      title: data.title || '',
      description: data.description || '',
      changeType: data.changeType || 'Scope Change',
      priority: data.priority || 'Medium',
      status: data.status || 'Draft',
      requestedBy: data.requestedBy?._id || data.requestedBy || '',
      requestedByCode: data.requestedByCode || data.requestedBy?.employeeCode || data.requestedBy?.code || '',
      requestedByName: data.requestedByName || (data.requestedBy ? `${data.requestedBy.firstName || ''} ${data.requestedBy.lastName || ''}`.trim() : ''),
      requestDate: data.requestDate ? new Date(data.requestDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      approver: data.approver?._id || data.approver || '',
      approverName: data.approverName || (data.approver ? `${data.approver.firstName || ''} ${data.approver.lastName || ''}`.trim() : ''),
      approvalDate: data.approvalDate ? new Date(data.approvalDate).toISOString().split('T')[0] : '',
      approvalComments: data.approvalComments || '',
      impactAnalysis: data.impactAnalysis || {
        scope: '',
        timeline: '',
        budget: 0,
        resources: '',
        risks: ''
      },
      implementationPlan: data.implementationPlan || '',
      estimatedEffort: data.estimatedEffort || 0,
      estimatedEffortUnit: data.estimatedEffortUnit || 'Days',
      estimatedCost: data.estimatedCost || 0,
      currency: data.currency || 'INR',
      requestedStartDate: data.requestedStartDate ? new Date(data.requestedStartDate).toISOString().split('T')[0] : '',
      requestedEndDate: data.requestedEndDate ? new Date(data.requestedEndDate).toISOString().split('T')[0] : '',
      actualStartDate: data.actualStartDate ? new Date(data.actualStartDate).toISOString().split('T')[0] : '',
      actualEndDate: data.actualEndDate ? new Date(data.actualEndDate).toISOString().split('T')[0] : '',
      assignedTo: data.assignedTo?._id || data.assignedTo || '',
      assignedToCode: data.assignedToCode || data.assignedTo?.employeeCode || data.assignedTo?.code || '',
      assignedToName: data.assignedToName || (data.assignedTo ? `${data.assignedTo.firstName || ''} ${data.assignedTo.lastName || ''}`.trim() : ''),
      attachments: data.attachments || [],
      notes: data.notes || '',
      remarks: data.remarks || ''
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }

    // Update project details when project changes
    if (name === 'project' && value) {
      const selectedProject = masterData.projects.find(p => (p._id || p.id) === value)
      if (selectedProject) {
        setFormData(prev => ({
          ...prev,
          project: value,
          projectNumber: selectedProject.projectNumber || '',
          projectName: selectedProject.projectName || ''
        }))
      }
    }

    // Update requested by name and code when employee changes
    if (name === 'requestedBy' && value) {
      const selectedEmployee = masterData.employees.find(e => (e._id || e.id) === value)
      if (selectedEmployee) {
        const employeeCode = selectedEmployee.employeeCode || selectedEmployee.code || ''
        const fullName = `${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim()
        setFormData(prev => ({
          ...prev,
          requestedBy: value,
          requestedByCode: employeeCode,
          requestedByName: fullName
        }))
      }
    }

    // Update assigned to name and code when employee changes
    if (name === 'assignedTo' && value) {
      const selectedEmployee = masterData.employees.find(e => (e._id || e.id) === value)
      if (selectedEmployee) {
        const employeeCode = selectedEmployee.employeeCode || selectedEmployee.code || ''
        const fullName = `${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim()
        setFormData(prev => ({
          ...prev,
          assignedTo: value,
          assignedToCode: employeeCode,
          assignedToName: fullName
        }))
      }
    }
  }

  const handleNestedInputChange = (parentKey, childKey, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.project) {
      newErrors.project = 'Project is required'
    }
    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Title is required'
    }
    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Description is required'
    }
    if (!formData.requestedBy) {
      newErrors.requestedBy = 'Requested by is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      const errorCount = Object.keys(errors).length
      toast.error(`Please fix ${errorCount} error(s) before submitting`)
      return
    }

    try {
      setLoading(true)
      let response
      
      const changeRequestData = {
        project: formData.project,
        projectNumber: formData.projectNumber,
        projectName: formData.projectName,
        title: formData.title.trim(),
        description: formData.description.trim(),
        changeType: formData.changeType,
        priority: formData.priority,
        status: formData.status,
        requestedBy: formData.requestedBy,
        requestedByCode: formData.requestedByCode,
        requestedByName: formData.requestedByName,
        requestDate: formData.requestDate,
        approver: formData.approver || null,
        approverName: formData.approverName,
        approvalDate: formData.approvalDate || null,
        approvalComments: formData.approvalComments,
        impactAnalysis: formData.impactAnalysis,
        implementationPlan: formData.implementationPlan,
        estimatedEffort: formData.estimatedEffort,
        estimatedEffortUnit: formData.estimatedEffortUnit,
        estimatedCost: formData.estimatedCost,
        currency: formData.currency,
        requestedStartDate: formData.requestedStartDate || null,
        requestedEndDate: formData.requestedEndDate || null,
        actualStartDate: formData.actualStartDate || null,
        actualEndDate: formData.actualEndDate || null,
        assignedTo: formData.assignedTo || null,
        assignedToCode: formData.assignedToCode || '',
        assignedToName: formData.assignedToName,
        attachments: formData.attachments,
        notes: formData.notes,
        remarks: formData.remarks
      }

      if (isEditMode) {
        response = await changeRequestService.updateChangeRequest(actualChangeRequestId, changeRequestData)
        if (response.success) {
          toast.success('Change request updated successfully')
          navigate('/projects/change-requests')
        } else {
          toast.error(response.message || 'Failed to update change request')
        }
      } else {
        response = await changeRequestService.createChangeRequest(changeRequestData)
        if (response.success) {
          toast.success('Change request created successfully')
          navigate('/projects/change-requests')
        } else {
          toast.error(response.message || 'Failed to create change request')
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} change request:`, error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} change request`)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      changeRequestNumber: '',
      project: '',
      projectNumber: '',
      projectName: '',
      title: '',
      description: '',
      changeType: 'Scope Change',
      priority: 'Medium',
      status: 'Draft',
      requestedBy: '',
      requestedByCode: '',
      requestedByName: '',
      requestDate: new Date().toISOString().split('T')[0],
      approver: '',
      approverName: '',
      approvalDate: '',
      approvalComments: '',
      impactAnalysis: {
        scope: '',
        timeline: '',
        budget: 0,
        resources: '',
        risks: ''
      },
      implementationPlan: '',
      estimatedEffort: 0,
      estimatedEffortUnit: 'Days',
      estimatedCost: 0,
      currency: 'INR',
      requestedStartDate: '',
      requestedEndDate: '',
      actualStartDate: '',
      actualEndDate: '',
      assignedTo: '',
      assignedToCode: '',
      assignedToName: '',
      attachments: [],
      notes: '',
      remarks: ''
    })
    setErrors({})
  }

  const handleTestData = () => {
    const today = new Date()
    const requestDate = today.toISOString().split('T')[0]
    const requestedStartDate = new Date(today)
    requestedStartDate.setDate(today.getDate() + 7)
    const requestedEndDate = new Date(today)
    requestedEndDate.setDate(today.getDate() + 30)
    
    const testProject = masterData.projects.length > 0 ? masterData.projects[0] : null
    const testEmployee = masterData.employees.length > 0 ? masterData.employees[0] : null

    setFormData(prev => ({
      ...prev,
      project: testProject ? (testProject._id || testProject.id) : '',
      projectNumber: testProject?.projectNumber || '',
      projectName: testProject?.projectName || 'Test Project',
      title: 'Add new feature module to existing project',
      description: 'Request to add a new reporting module to the existing project. This will include dashboard creation, data visualization, and export functionality.',
      changeType: 'Scope Change',
      priority: 'High',
      status: 'Draft',
      requestedBy: testEmployee ? (testEmployee._id || testEmployee.id) : '',
      requestedByCode: testEmployee ? (testEmployee.employeeCode || testEmployee.code || '') : '',
      requestedByName: testEmployee ? `${testEmployee.firstName || ''} ${testEmployee.lastName || ''}`.trim() : 'Test Employee',
      requestDate: requestDate,
      impactAnalysis: {
        scope: 'Additional 3 modules need to be developed: Dashboard, Reports, and Export functionality',
        timeline: 'Estimated 4-6 weeks additional time required',
        budget: 150000,
        resources: 'Need 2 additional developers and 1 QA resource',
        risks: 'May impact existing project timeline and budget. Requires stakeholder approval.'
      },
      implementationPlan: 'Phase 1: Design and architecture (Week 1-2)\nPhase 2: Development (Week 3-5)\nPhase 3: Testing and deployment (Week 6)',
      estimatedEffort: 30,
      estimatedEffortUnit: 'Days',
      estimatedCost: 150000,
      currency: 'INR',
      requestedStartDate: requestedStartDate.toISOString().split('T')[0],
      requestedEndDate: requestedEndDate.toISOString().split('T')[0],
      assignedTo: testEmployee ? (testEmployee._id || testEmployee.id) : '',
      assignedToCode: testEmployee ? (testEmployee.employeeCode || testEmployee.code || '') : '',
      assignedToName: testEmployee ? `${testEmployee.firstName || ''} ${testEmployee.lastName || ''}`.trim() : 'Test Employee',
      notes: 'This change request is for testing purposes. Please review and approve.',
      remarks: 'Test change request data'
    }))
    
    setErrors({})
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Change Requests', path: '/projects/change-requests', onClick: () => navigate('/projects/change-requests') },
    { label: isEditMode ? 'Edit Change Request' : isViewMode ? 'View Change Request' : 'Create New', path: isEditMode ? `/projects/change-requests/edit/${actualChangeRequestId}` : isViewMode ? `/projects/change-requests/view/${actualChangeRequestId}` : '/projects/change-requests/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: FileText },
    { id: 'impact', label: 'Impact Analysis', icon: AlertCircle },
    { id: 'implementation', label: 'Implementation', icon: Settings },
    { id: 'notes', label: 'Notes & Remarks', icon: FileText }
  ]

  const helpContent = (
    <>
      <h3>What is Change Request Entry?</h3>
      <p>Change Request Entry lets you <strong>create and manage project scope and requirement changes</strong>. You describe the change, impact (schedule, cost, scope), implementation details, and track status and approval.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Change request number, project, title, description, type, priority, status, requested by, date.</p>
      <p><strong>Impact Analysis</strong> – Schedule impact, cost impact, scope impact, risk, and approval workflow.</p>
      <p><strong>Implementation</strong> – Implementation plan, assignee, target date.</p>
      <p><strong>Notes &amp; Remarks</strong> – Notes and remarks.</p>
      <h3>How to use</h3>
      <p>Select a <strong>project</strong>, enter title and description of the change. Fill impact and implementation as needed. Use <strong>Save</strong> to create or update. Submit for approval from the list when required.</p>
    </>
  )

  if (changeRequestDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading change request data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Change Request' : isViewMode ? 'View Change Request' : 'Create Change Request'}</h1>
          <p>{isEditMode ? 'Update change request information' : isViewMode ? 'View change request details' : 'Create and manage change requests'}</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Change Request Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${isViewMode ? 'cursor-pointer' : ''}`}
                onClick={() => !isReadOnly || isViewMode ? setActiveTab(tab.id) : undefined}
                disabled={!isReadOnly && false}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>
          
        <form className="form-content" onSubmit={handleSubmit}>
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="changeRequestNumber" className="form-label">
                    Change Request #
                  </label>
                  <input
                    type="text"
                    id="changeRequestNumber"
                    name="changeRequestNumber"
                    value={formData.changeRequestNumber || 'Auto-generated on save'}
                    readOnly
                    className="form-input"
                    placeholder="Auto-generated on save"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed', fontStyle: 'italic' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="project" className="form-label required">
                    Project
                  </label>
                  <select
                    id="project"
                    name="project"
                    value={formData.project}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  >
                    <option value="">Select Project</option>
                    {masterData.projects.map(project => (
                      <option key={project._id || project.id} value={project._id || project.id}>
                        {project.projectNumber} - {project.projectName}
                      </option>
                    ))}
                  </select>
                  {errors.project && (
                    <p className="form-error">{errors.project}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="requestDate" className="form-label required">
                    Request Date
                  </label>
                  <input
                    type="date"
                    id="requestDate"
                    name="requestDate"
                    value={formData.requestDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  />
                  {errors.requestDate && (
                    <p className="form-error">{errors.requestDate}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="title" className="form-label required">
                    Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                    placeholder="Enter change request title"
                  />
                  {errors.title && (
                    <p className="form-error">{errors.title}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="changeType" className="form-label required">
                    Change Type
                  </label>
                  <select
                    id="changeType"
                    name="changeType"
                    value={formData.changeType}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  >
                    <option value="Scope Change">Scope Change</option>
                    <option value="Requirement Change">Requirement Change</option>
                    <option value="Timeline Change">Timeline Change</option>
                    <option value="Budget Change">Budget Change</option>
                    <option value="Resource Change">Resource Change</option>
                    <option value="Technical Change">Technical Change</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label required">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isReadOnly}
                  required
                  rows={4}
                  placeholder="Enter detailed description of the change request"
                />
                {errors.description && (
                  <p className="form-error">{errors.description}</p>
                )}
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="priority" className="form-label required">
                    Priority
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="requestedBy" className="form-label required">
                    Requested By
                  </label>
                  <select
                    id="requestedBy"
                    name="requestedBy"
                    value={formData.requestedBy}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  >
                    <option value="">Select Employee</option>
                    {masterData.employees.map(employee => {
                      const employeeCode = employee.employeeCode || employee.code || ''
                      const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
                      const displayText = fullName 
                        ? `${employeeCode} - ${fullName}`
                        : employeeCode || 'Employee'
                      return (
                        <option key={employee._id || employee.id} value={employee._id || employee.id}>
                          {displayText}
                        </option>
                      )
                    })}
                  </select>
                  {errors.requestedBy && (
                    <p className="form-error">{errors.requestedBy}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="assignedTo" className="form-label">
                    Assigned To
                  </label>
                  <select
                    id="assignedTo"
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="">Select Employee</option>
                    {masterData.employees.map(employee => {
                      const employeeCode = employee.employeeCode || employee.code || ''
                      const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
                      const displayText = fullName 
                        ? `${employeeCode} - ${fullName}`
                        : employeeCode || 'Employee'
                      return (
                        <option key={employee._id || employee.id} value={employee._id || employee.id}>
                          {displayText}
                        </option>
                      )
                    })}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>
              </div>

              {/* Approval Information (Read-only in view mode) */}
              {(isViewMode || isEditMode) && formData.approverName && (
                <>
                  <div className="section-header" style={{ marginTop: '24px' }}>
                    <h3>Approval Information</h3>
                  </div>
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label htmlFor="approverName" className="form-label">
                        Approver
                      </label>
                      <input
                        type="text"
                        id="approverName"
                        name="approverName"
                        value={formData.approverName}
                        readOnly
                        className="form-input"
                        style={{ backgroundColor: '#f9fafb' }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="approvalDate" className="form-label">
                        Approval Date
                      </label>
                      <input
                        type="date"
                        id="approvalDate"
                        name="approvalDate"
                        value={formData.approvalDate}
                        readOnly
                        className="form-input"
                        style={{ backgroundColor: '#f9fafb' }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="approvalComments" className="form-label">
                        Approval Comments
                      </label>
                      <textarea
                        id="approvalComments"
                        name="approvalComments"
                        value={formData.approvalComments}
                        readOnly
                        className="form-input"
                        rows={2}
                        style={{ backgroundColor: '#f9fafb' }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Impact Analysis Tab */}
          {activeTab === 'impact' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Impact Analysis</h3>
              </div>
              
              <div className="form-group">
                <label htmlFor="impactScope" className="form-label">
                  Scope Impact
                </label>
                <textarea
                  id="impactScope"
                  name="impactScope"
                  value={formData.impactAnalysis.scope}
                  onChange={(e) => handleNestedInputChange('impactAnalysis', 'scope', e.target.value)}
                  className="form-input"
                  disabled={isReadOnly}
                  rows={3}
                  placeholder="Describe the impact on project scope"
                />
              </div>

              <div className="form-group">
                <label htmlFor="impactTimeline" className="form-label">
                  Timeline Impact
                </label>
                <textarea
                  id="impactTimeline"
                  name="impactTimeline"
                  value={formData.impactAnalysis.timeline}
                  onChange={(e) => handleNestedInputChange('impactAnalysis', 'timeline', e.target.value)}
                  className="form-input"
                  disabled={isReadOnly}
                  rows={3}
                  placeholder="Describe the impact on project timeline"
                />
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="impactBudget" className="form-label">
                    Budget Impact
                  </label>
                  <input
                    type="number"
                    id="impactBudget"
                    name="impactBudget"
                    value={formData.impactAnalysis.budget}
                    onChange={(e) => handleNestedInputChange('impactAnalysis', 'budget', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    disabled={isReadOnly}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="impactResources" className="form-label">
                    Resource Impact
                  </label>
                  <textarea
                    id="impactResources"
                    name="impactResources"
                    value={formData.impactAnalysis.resources}
                    onChange={(e) => handleNestedInputChange('impactAnalysis', 'resources', e.target.value)}
                    className="form-input"
                    disabled={isReadOnly}
                    rows={3}
                    placeholder="Describe the impact on resources"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="impactRisks" className="form-label">
                  Risks
                </label>
                <textarea
                  id="impactRisks"
                  name="impactRisks"
                  value={formData.impactAnalysis.risks}
                  onChange={(e) => handleNestedInputChange('impactAnalysis', 'risks', e.target.value)}
                  className="form-input"
                  disabled={isReadOnly}
                  rows={3}
                  placeholder="Describe potential risks associated with this change"
                />
              </div>
            </div>
          )}

          {/* Implementation Tab */}
          {activeTab === 'implementation' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Implementation Details</h3>
              </div>
              
              <div className="form-group">
                <label htmlFor="implementationPlan" className="form-label">
                  Implementation Plan
                </label>
                <textarea
                  id="implementationPlan"
                  name="implementationPlan"
                  value={formData.implementationPlan}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isReadOnly}
                  rows={5}
                  placeholder="Enter detailed implementation plan"
                />
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="estimatedEffort" className="form-label">
                    Estimated Effort
                  </label>
                  <input
                    type="number"
                    id="estimatedEffort"
                    name="estimatedEffort"
                    value={formData.estimatedEffort}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    min="0"
                    step="0.01"
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="estimatedEffortUnit" className="form-label">
                    Effort Unit
                  </label>
                  <select
                    id="estimatedEffortUnit"
                    name="estimatedEffortUnit"
                    value={formData.estimatedEffortUnit}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="Hours">Hours</option>
                    <option value="Days">Days</option>
                    <option value="Weeks">Weeks</option>
                    <option value="Months">Months</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="estimatedCost" className="form-label">
                    Estimated Cost
                  </label>
                  <input
                    type="number"
                    id="estimatedCost"
                    name="estimatedCost"
                    value={formData.estimatedCost}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="requestedStartDate" className="form-label">
                    Requested Start Date
                  </label>
                  <input
                    type="date"
                    id="requestedStartDate"
                    name="requestedStartDate"
                    value={formData.requestedStartDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="requestedEndDate" className="form-label">
                    Requested End Date
                  </label>
                  <input
                    type="date"
                    id="requestedEndDate"
                    name="requestedEndDate"
                    value={formData.requestedEndDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="actualStartDate" className="form-label">
                    Actual Start Date
                  </label>
                  <input
                    type="date"
                    id="actualStartDate"
                    name="actualStartDate"
                    value={formData.actualStartDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="actualEndDate" className="form-label">
                    Actual End Date
                  </label>
                  <input
                    type="date"
                    id="actualEndDate"
                    name="actualEndDate"
                    value={formData.actualEndDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes & Remarks Tab */}
          {activeTab === 'notes' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Notes & Remarks</h3>
              </div>
              
              <div className="form-group">
                <label htmlFor="notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isReadOnly}
                  rows={6}
                  placeholder="Enter any additional notes or comments"
                />
              </div>

              <div className="form-group">
                <label htmlFor="remarks" className="form-label">
                  Remarks
                </label>
                <textarea
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isReadOnly}
                  rows={4}
                  placeholder="Enter any remarks"
                />
              </div>
            </div>
          )}

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions">
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleReset}
                  disabled={loading}
                >
                  <RotateCcw size={16} />
                  Reset
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/projects/change-requests')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  ) : (
                    <span>{isEditMode ? 'Update Change Request' : 'Create Change Request'}</span>
                  )}
                </button>
              </div>
            </div>
          )}
          {isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/projects/change-requests')}
              >
                Back
              </button>
            </div>
          )}
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Change Request Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ChangeRequestForm

